import React, { useEffect, useState } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import {
  Column,
  FormController,
  MuiBox,
  MuiButtonSecondary,
  MuiGrid,
  MuiOptionMenu,
  MuiSelect,
  MuiTable,
  MuiTextField,
  MuiTypography,
  OptionsDataType,
  RowProps,
} from "../../../../../../../../../components";
import {
  buildActionMenuItems,
  formatApiDataToDropDownOption,
  getValueOrDash,
  isNotEmpty,
} from "../../../../../../../../../helpers/utils";
import { useSiteCore } from "../../../../../../../../../hooks/useSiteCore";

import {
  EMPTY_VALUE_DASH,
  MAX_PERCENTAGE,
  MODULE_PERMISSIONS,
} from "../../../../../../../../../helpers/constants";
import {
  MenuItemConfig,
  UserDetails,
} from "../../../../../../../../../helpers/types/common";
import { useGridContainer } from "../../../../../../../../../hooks/useGridContainer";
import { useNavigationSiteCore } from "../../../../../../../../../hooks/useNavigationSiteCore";
import { SalesCreditDetailsFormKeys } from "../../../../../../../../createProgram/modelsAndSchemas/Models";
import {
  FIXED_PROJECT_SALES_CREDIT_PERCENTAGE,
  MAP_33_34_67,
  MAX_PROJECT_SALES_CREDIT_ENTRIES,
  VALUE_MAPPER,
} from "../../../../../../../helper";
import {
  ProjectSalesCreditDetailsFormKeys,
  SalesCreditTableRow,
} from "../../../../../../../modelsAndSchemas/Models";
import { salesCreditSiteCoreKeys } from "../../../../../../../siteCore/SiteCoreKeys";
import { SalesCreditDetailsTableWrapper } from "./SalesCreditDetailsTable.style";
import { SalesCreditDetailsTableHeader } from "./salesCreditDetailsTableHeader/SalesCreditDetailsTableHeader";
import { usePermissions } from "../../../../../../../../../hooks/usePermissions";

interface ProjectSalesCreditDetailsTableProps {
  pageId: string;
  addSalesCredit: () => void;
  filteredResults: SalesCreditTableRow[];
  onDeleteSalesCredit: (row: SalesCreditTableRow) => void;
  onSalesCreditAddedCallback: boolean;
  clientRelationshipOwnerList?: UserDetails[];
  onSalesCreditDeletedCallback: boolean;
  isInViewMode?: boolean;
  showDefaultEntry: boolean;
}

export const calculateTotalPercentage = (
  filteredResults: SalesCreditTableRow[],
) => {
  let totalAmountOrPercentage = 0;
  filteredResults.forEach((item) => {
    totalAmountOrPercentage += parseInt(item?.percentage ?? "0");
  });
  return totalAmountOrPercentage;
};

export const ProjectSalesCreditDetailsTable: React.FC<
  ProjectSalesCreditDetailsTableProps
> = ({
  addSalesCredit,
  filteredResults,
  onDeleteSalesCredit,
  onSalesCreditAddedCallback,
  clientRelationshipOwnerList,
  onSalesCreditDeletedCallback,
  pageId,
  showDefaultEntry = false,
  isInViewMode = false,
}) => {
  const { commonLabels, fetchLabel } = useSiteCore();

  const { control, setValue } = useFormContext();

  const watchClientRelationshipOwner = useWatch({
    control,
    name: SalesCreditDetailsFormKeys.clientRelationshipOwner,
  });

  const { gridSizeMedium: gridSize } = useGridContainer();

  const { commonARIALabel } = useNavigationSiteCore();
  const { hasModulePermission } = usePermissions();

  const getLabel = (key: string) => {
    return fetchLabel(salesCreditSiteCoreKeys.salesCredit, key);
  };

  const clientRelationshipOwnerFormKey =
    ProjectSalesCreditDetailsFormKeys.clientRelationshipOwner;
  const percentageFormKey = ProjectSalesCreditDetailsFormKeys.percentage;
  const officeNameFormKey = ProjectSalesCreditDetailsFormKeys.officeName;

  const [
    clientRelationshipOwnerDropdownOptions,
    setClientRelationshipOwnerDropdownOptions,
  ] = useState<OptionsDataType[]>([]);

  const [percentageDropdownOptions, setPercentageDropdownOptions] = useState<
    OptionsDataType[]
  >(FIXED_PROJECT_SALES_CREDIT_PERCENTAGE);

  const onClientRelationshipOwnerChange = (selectedValue: string) => {
    const selectedClientRelationshipOwnerData =
      clientRelationshipOwnerList?.find(
        (clientRelationshipOwner) =>
          clientRelationshipOwner.id === selectedValue,
      );

    if (isNotEmpty(selectedClientRelationshipOwnerData)) {
      const officeNameValue = getValueOrDash(
        selectedClientRelationshipOwnerData?.officeName,
      );

      setValue(officeNameFormKey, officeNameValue);
    }
  };

  useEffect(() => {
    onClientRelationshipOwnerChange(watchClientRelationshipOwner);
  }, [watchClientRelationshipOwner]);

  useEffect(() => {
    setClientRelationshipOwnerDropdownOptions(
      formatApiDataToDropDownOption(clientRelationshipOwnerList ?? []),
    );
  }, [clientRelationshipOwnerList]);

  const updatePercentageDropdownData = () => {
    const existingFilteredResults = [...filteredResults];

    if (existingFilteredResults.length === 1) {
      const [first] = existingFilteredResults;
      const mappedValues = VALUE_MAPPER[first.percentage] || [];
      setPercentageDropdownOptions(
        mappedValues.map((value) => ({
          label: value.toString(),
          value: value,
        })),
      );
      return;
    }

    if (existingFilteredResults.length === 2) {
      const totalAssigned = existingFilteredResults.reduce(
        (sum, item) => sum + (parseInt(item.percentage ?? "0") || 0),
        0,
      );
      const remaining = MAX_PERCENTAGE - totalAssigned;
      setPercentageDropdownOptions(
        MAP_33_34_67.filter((option) => Number(option.value) === remaining),
      );
      return;
    }

    setPercentageDropdownOptions(
      existingFilteredResults.length === 0
        ? FIXED_PROJECT_SALES_CREDIT_PERCENTAGE
        : [],
    );
  };

  useEffect(() => {
    updateClientRelationshipOwnerDropdownData();
    updatePercentageDropdownData();
  }, [
    onSalesCreditAddedCallback,
    onSalesCreditDeletedCallback,
    filteredResults,
    clientRelationshipOwnerList,
  ]);

  const updateClientRelationshipOwnerDropdownData = () => {
    const filteredResultsCopy = [...filteredResults];

    const uniqueClientRelationshipOwner = (
      clientRelationshipOwnerList ?? []
    ).filter((clientRelationshipOwner) => {
      return !filteredResultsCopy.some((result) =>
        result.localClientRelationshipOwnerId?.includes(
          clientRelationshipOwner.id,
        ),
      );
    });

    setClientRelationshipOwnerDropdownOptions(
      formatApiDataToDropDownOption(uniqueClientRelationshipOwner ?? []),
    );
  };

  const columns: Column[] = [
    {
      id: clientRelationshipOwnerFormKey,
      label: getLabel(
        salesCreditSiteCoreKeys.clientRelationshipOwnerTableColumn,
      ),
      isSortVisible: false,
      render: (row: any) => (
        <MuiBox>
          <MuiTypography variant="h3">
            {row.clientRelationshipOwner}
          </MuiTypography>
        </MuiBox>
      ),
    },
    {
      id: officeNameFormKey,
      label: getLabel(salesCreditSiteCoreKeys.officeTableColumn),
      isSortVisible: false,
      render: (row: any) => (
        <MuiBox>
          <MuiTypography variant="h3">{row.officeName}</MuiTypography>
        </MuiBox>
      ),
    },
    {
      id: percentageFormKey,
      label: getLabel(salesCreditSiteCoreKeys.percentageTableColumn),
      isSortVisible: false,
      render: (row: any) => (
        <MuiBox>
          <MuiTypography variant="h3">{`${row.percentage}%`}</MuiTypography>
        </MuiBox>
      ),
    },
    ...(!isInViewMode
      ? [
          {
            id: "action",
            label: getLabel(salesCreditSiteCoreKeys.actionTableColumn),
            render: (row: any) => {
              const configs: MenuItemConfig<RowProps>[] = [
                {
                  permissionKey: MODULE_PERMISSIONS.PROJECT_EDIT_SALES_CREDIT,
                  create: (r) => ({
                    label: commonLabels.delete,
                    dataTestId: `${pageId}-delete`,
                    onClick: () => onDeleteSalesCredit(row),
                  }),
                },
              ];

              const menuItems = buildActionMenuItems(
                row,
                configs,
                hasModulePermission,
              );

              return menuItems ? <MuiOptionMenu menuItems={menuItems} /> : null;
            },
          },
        ]
      : []),
  ];

  const disabledAddButton = () => {
    return (
      showDefaultEntry ||
      ((filteredResults.length >= MAX_PROJECT_SALES_CREDIT_ENTRIES ||
        calculateTotalPercentage(filteredResults) >= MAX_PERCENTAGE) &&
        filteredResults.length !== 1)
    );
  };

  return (
    <SalesCreditDetailsTableWrapper>
      {!isInViewMode ? (
        <>
          <SalesCreditDetailsTableHeader
            header={getLabel(
              salesCreditSiteCoreKeys.addClientRelationshipOwnerSubHeading,
            )}
          />
          <MuiBox className="sales-credit-configuration-middle">
            <MuiGrid container spacing={2}>
              <MuiGrid size={gridSize}>
                <FormController
                  control={control}
                  name={clientRelationshipOwnerFormKey}
                >
                  <MuiSelect
                    id={`${pageId}-${clientRelationshipOwnerFormKey}`}
                    label={getLabel(
                      salesCreditSiteCoreKeys.clientRelationshipOwnerDropDown,
                    )}
                    options={clientRelationshipOwnerDropdownOptions}
                    visibleDefaultSelectOption={true}
                    showSearch
                    placeholder={commonLabels.selectPlaceHolder}
                    isMandatory
                    requiredARIALabel={commonARIALabel.required}
                    disabled={disabledAddButton()}
                  />
                </FormController>
              </MuiGrid>
              <MuiGrid size={gridSize}>
                <FormController control={control} name={officeNameFormKey}>
                  <MuiTextField
                    id={`${pageId}-${officeNameFormKey}`}
                    label={getLabel(salesCreditSiteCoreKeys.office)}
                    placeholder={EMPTY_VALUE_DASH}
                    disabled
                    requiredARIALabel={commonARIALabel.required}
                  />
                </FormController>
              </MuiGrid>

              <MuiGrid size={gridSize}>
                <FormController control={control} name={percentageFormKey}>
                  <MuiSelect
                    id={`${pageId}-${percentageFormKey}`}
                    label={getLabel(salesCreditSiteCoreKeys.percentageDropDown)}
                    options={percentageDropdownOptions}
                    visibleDefaultSelectOption={true}
                    placeholder={commonLabels.selectPlaceHolder}
                    isMandatory
                    requiredARIALabel={commonARIALabel.required}
                    disabled={disabledAddButton()}
                  />
                </FormController>
              </MuiGrid>
            </MuiGrid>

            <MuiBox className="sales-credit-configuration-add-button">
              <MuiButtonSecondary
                onClick={addSalesCredit}
                data-testId={`${pageId}-add-sales-credit`}
                disabled={disabledAddButton()}
              >
                {commonLabels.add}
              </MuiButtonSecondary>
            </MuiBox>
          </MuiBox>
        </>
      ) : null}
      <MuiBox className="sales-credit-configuration-total-amount-percentage-container">
        <MuiTypography variant="h3Bold">
          {getLabel(salesCreditSiteCoreKeys.totalPercentage)}
        </MuiTypography>

        <MuiGrid className="sales-credit-configuration-total-amount-percentage">
          <MuiTypography variant="h3Bold">
            {`${calculateTotalPercentage(filteredResults)}%`}
          </MuiTypography>
        </MuiGrid>
      </MuiBox>
      <MuiBox className="sales-credit-configuration-table">
        <MuiTable
          pageId={pageId}
          tableHeaderForm={[]}
          columns={columns}
          tableResults={filteredResults}
          noDataInApi={filteredResults.length === 0}
          noResultMessage={commonLabels.noResult}
          emptyMessage={getLabel(
            salesCreditSiteCoreKeys.noClientRelationshipOwnersAvailable,
          )}
          priorityResults={[]}
          isFooterVisible={false}
        />
      </MuiBox>
    </SalesCreditDetailsTableWrapper>
  );
};
